import { useInfiniteQuery } from '@tanstack/react-query';
import { stringify } from 'query-string';
import React, { useEffect, useState, useMemo, Component } from 'react';
import { useAuth, AuthProvider } from '@dashboard/context/AuthContext/AuthContext';
import { fetchInfinite } from '@dashboard/services/request';
import { CheckoutEmbedMeasurer } from '@/dashboard/scenes/PageEdit/components/Preview';
import { checkoutTypes } from '@repo/checkout/enums/checkout-types';
import storage from '@dashboard/services/storage';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render shows the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details to an error tracking service or console
    console.error('Error caught by Error Boundary:', error);
    console.error('Error info:', errorInfo);

    // Store the failed page in local storage
    const failedPages = JSON.parse(storage.local.getItem('failedToMeasurePages') ?? '[]');
    if (this.props.currentPage) {
      failedPages.push(this.props.currentPage);
      storage.local.setItem('failedToMeasurePages', JSON.stringify(failedPages));
    }

    // Call the provided onError callback to continue to the next page
    if (this.props.onError) {
      this.props.onError();
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div>
          <p>Error occurred, skipping this page.</p>
        </div>
      );
    }

    return this.props.children;
  }
}

const CalculatePageMetadata = () => {
  const { token } = useAuth();
  const sortBy = 'updatedAt';
  const sortDirection = -1;

  const { data, fetchNextPage, isLoading, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['pages', sortBy, sortDirection],
    queryFn: async ({ pageParam }) =>
      fetchInfinite(
        `/admin/full-pages?${stringify({
          sort: JSON.stringify({
            [sortBy]: sortDirection,
          }),
          limit: 20,
          skip: pageParam * 20,
          filter: JSON.stringify({
            measuredAt: { $eq: null },
          }),
        })}`,
        token,
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;

      return allPages.length;
    },
    enabled: false,
  });

  const allPages = useMemo(() => data?.pages.flatMap(p => p.data) ?? [], [data]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  const total = data?.pages[0]?.total;

  return (
    <div className="py-8">
      <div className="flex items-end justify-end gap-x-2">
        <div className="mr-auto">{total} pages</div>
      </div>
      <div>
        <CheckoutMeasurementRunner
          pages={allPages}
          checkoutType={checkoutTypes.EMBED}
          isConfirmation={false}
          fetchNextPage={fetchNextPage}
          hasNextPage={data?.pages[data.pages.length - 1]?.has_more}
          isFetchingNextPage={isFetchingNextPage}
        />
      </div>
    </div>
  );
};

export default CalculatePageMetadata;

function CheckoutMeasurementRunner({
  pages,
  checkoutType,
  isConfirmation,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
}) {
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [isRunning, setIsRunning] = useState(false); // Default to false
  const { token } = useAuth();

  const [measuredPages, setMeasuredPages] = useState(() => {
    return JSON.parse(storage.local.getItem('measuredPages') ?? '[]');
  });

  const [failedPages, setFailedPages] = useState(() => {
    return JSON.parse(storage.local.getItem('failedToMeasurePages') ?? '[]');
  });

  const totalMeasuredPages = measuredPages.length;
  const totalFailedPages = failedPages.length;

  const markPagesAsMeasured = pageIds => {
    const updated = [...measuredPages, ...pageIds];
    setMeasuredPages(updated);
    storage.local.setItem('measuredPages', JSON.stringify(updated));
  };

  const markPagesAsFailed = pageIds => {
    const updated = [...failedPages, ...pageIds];
    setFailedPages(updated);
    storage.local.setItem('failedToMeasurePages', JSON.stringify(updated));
  };

  const queryClient = new QueryClient();

  const measurePage = async (page, checkoutType, isConfirmation) => {
    return new Promise((resolve, reject) => {
      const measurer = document.createElement('div');
      document.body.appendChild(measurer);

      const root = ReactDOM.createRoot(measurer);

      const onMeasurementsDone = success => {
        setTimeout(() => {
          root.unmount();
          document.body.removeChild(measurer);
          if (success === true) {
            resolve();
          } else {
            reject(new Error('Measurements failed'));
          }
        }, 0); // Defer unmounting to avoid race conditions
      };

      const onError = error => {
        setTimeout(() => {
          root.unmount();
          document.body.removeChild(measurer);
          reject(error);
        }, 0); // Defer unmounting to avoid race conditions
      };

      // Fail silently for invalid seller data
      if (!page.seller || !page.seller.name) {
        setTimeout(() => {
          root.unmount();
          document.body.removeChild(measurer);
          reject(new Error('Invalid seller data'));
        }, 0);
        return;
      }

      // Validate the page object before rendering
      if (!page || !page.slug) {
        setTimeout(() => {
          root.unmount();
          document.body.removeChild(measurer);
          reject(new Error('Invalid page data'));
        }, 0);
        return;
      }

      root.render(
        <BrowserRouter>
          <QueryClientProvider client={queryClient}>
            <CheckoutEmbedMeasurer
              page={page}
              checkoutType={checkoutType}
              isConfirmation={isConfirmation}
              pathname={`/embed/${page.seller.name}/${page.slug}`}
              onMeasurementsDone={onMeasurementsDone}
              onError={onError}
              isAdminMigration={true}
              measurementCoolDown={800}
              token={token}
            />
          </QueryClientProvider>
        </BrowserRouter>,
      );
    });
  };

  const processPagesInParallel = async () => {
    const batchSize = 20; // Set batch size to 10
    const batch = pages
      .slice(currentPageIndex, currentPageIndex + batchSize)
      .filter(page => !measuredPages.includes(page._id))
      .filter(page => !failedPages.includes(page._id));

    const measuredPageIds = [];
    const failedPageIds = [];

    await Promise.all(
      batch.map(async page => {
        try {
          await measurePage(page, checkoutType, isConfirmation);
          measuredPageIds.push(page._id);
        } catch (error) {
          if (error.message !== 'Invalid seller data') {
            console.log('page', page);
            console.error(`Failed to measure page: ${page._id} ${error}`, error);
          }
          failedPageIds.push(page._id);
        }
      }),
    );

    if (measuredPageIds.length > 0) {
      markPagesAsMeasured(measuredPageIds);
    }

    if (failedPageIds.length > 0) {
      markPagesAsFailed(failedPageIds);
    }

    console.log('Batch processed:', batch);
    setCurrentPageIndex(currentPageIndex + batchSize);
    setIsFetchingMore(true);
    await fetchNextPage();
    setIsFetchingMore(false);

    // Stop running if there are no more pages
    if (!hasNextPage && currentPageIndex + batchSize >= pages.length) {
      setIsRunning(false);
    }
  };

  useEffect(() => {
    if (isRunning && !isFetchingMore) {
      processPagesInParallel();
    }
  }, [isRunning, currentPageIndex, pages, isFetchingMore]);

  return (
    <div>
      <div className="mb-4 flex gap-4">
        <button
          className="px-4 py-2 bg-blue-600 text-white rounded"
          onClick={() => setIsRunning(prev => !prev)}
        >
          {isRunning ? '⏸ Stop' : '▶️ Start'}
        </button>

        <button
          className="px-4 py-2 bg-red-500 text-white rounded"
          onClick={() => {
            storage.local.removeItem('measuredPages');
            storage.local.removeItem('failedToMeasurePages');
            setMeasuredPages([]);
            setFailedPages([]);
            setCurrentPageIndex(0);
            setIsRunning(false); // Reset isRunning to false
          }}
        >
          🔄 Reset
        </button>
      </div>

      <div className="mb-4">
        <p>
          Total pages measured: <strong>{totalMeasuredPages}</strong>
        </p>
        <p>
          Total pages failed: <strong>{totalFailedPages}</strong>
        </p>
      </div>

      {isFetchingNextPage && <div>🔄 Fetching next page...</div>}
      {!isFetchingMore && !hasNextPage && currentPageIndex >= pages.length && (
        <div>
          ✅ All pages processed
          <p>No more pages are available for measurement. You can reset to start over.</p>
        </div>
      )}
    </div>
  );
}
